<script setup lang="ts">
import RewardModal from './RewardModal.vue'

import { calculateTotalProbability } from '../../utils'
import type { Contest, ContestGameReward } from './types/global'

const props = defineProps<{
  getContest: Contest
  isEditMode: boolean
}>()

const notification = useNotification()
const { t } = useI18n()

const contest = ref(props.getContest)
const sumPercentage = ref(0)

const cardLength = computed(() => {
  return contest.value.canLose ? contest.value.contestGameRewards.length + 1 : contest.value.contestGameRewards.length
})

const computedWheelRewards = computed(() => {
  const wheelRewards = contest.value.contestGameRewards.map((reward, index) => {
    return {
      id: index + 1,
      bgColor: reward.color,
      color: reward.textColor,
      value: reward.name,
      probability: reward.probability,
      hasQrcode: reward.hasQrcode
    }
  })
  if (contest.value.canLose) {
    wheelRewards.push({
      id: wheelRewards.length + 1,
      bgColor: contest.value.losingTileColor,
      color: contest.value.losingTileTextColor,
      value: contest.value.losingTileText,
      probability: contest.value.losingProbability,
      hasQrcode: false
    })
  }
  return wheelRewards
})

const rewardNameMaximumCharacters = computed(() => {
  if (computedWheelRewards.value.length >= 7) {
    return 20
  } else if (computedWheelRewards.value.length === 6) {
    return 24
  } else {
    return 30
  }
})

const canAddMoreReward = computed(() => {
  return contest.value.contestGameRewards.length < maximumRewardsCount.value
})

const maximumRewardsCount = computed(() => {
  return contest.value.canLose ? 7 : 8
})

const removeReward = (index: number) => {
  if (index === contest.value.contestGameRewards.length) {
    canLose(false)
  }
  contest.value.contestGameRewards = contest.value.contestGameRewards.filter((value, i) => i !== index)

  calculateSumPercentage()
}

const calculateSumPercentage = () => {
  if (computedWheelRewards.value.length === 0) {
    return (sumPercentage.value = 0)
  }

  sumPercentage.value = calculateTotalProbability(computedWheelRewards.value)
}
const probabilityHasError = (probability = 0) => {
  const isProbabilityExceeded = sumPercentage.value > 100
  const isProbabilityFull = sumPercentage.value === 100
  const isRewardProbabilityEmpty = !probability || probability === 0
  return isProbabilityExceeded || (isProbabilityFull && isRewardProbabilityEmpty)
}

const addReward = (cardType: string) => {
  const newIndex = contest.value.contestGameRewards.length + 1
  if (canAddMoreReward.value) {
    contest.value.contestGameRewards.push({
      name: '',
      points: cardType === 'points' ? 0 : null,
      hasQrcode: cardType === 'gift',
      probability: null,
      color: newIndex % 2 === 0 ? contest.value.backgroundColor : contest.value.buttonColor,
      textColor: newIndex % 2 === 0 ? contest.value.textColor : contest.value.backgroundColor,
      notificationTextOverride: null,
      rewardTypeSlug: cardType === 'gift' ? 'free_product' : 'points',
      rewardTypeId: null,
      externalId: null,
      value: null,
      validityInDays: null,
      minimumOrderAmount: null
    })
  }
}

const updateReward = (reward: ContestGameReward, index: number) => {
  contest.value.contestGameRewards[index] = reward
}

const addLosingCard = () => {
  canLose(true)
  calculateSumPercentage()
}

const canLose = (canLose: boolean) => {
  contest.value.canLose = canLose
}

const duplicateCard = (index: number) => {
  if (!canAddMoreReward.value) {
    notification.create('error', {
      text: t('pages.contests.contestsCreatePage.prices.form.price.maximumReward')
    })
    return
  }
  const newIndex = contest.value.contestGameRewards.length
  const contestGameRewardsIndex = contest.value.contestGameRewards[index]
  if (index !== newIndex) {
    const duplicatedContest = {
      name: contestGameRewardsIndex.name,
      points: contestGameRewardsIndex.points,
      hasQrcode: contestGameRewardsIndex.hasQrcode,
      probability: contestGameRewardsIndex.probability,
      color: newIndex % 2 === 0 ? contest.value.backgroundColor : contest.value.buttonColor,
      textColor: contest.value.textColor,
      notificationTextOverride: contestGameRewardsIndex.notificationTextOverride,
      rewardTypeSlug: contestGameRewardsIndex.rewardTypeSlug,
      rewardTypeId: contestGameRewardsIndex?.rewardTypeId,
      externalId: contestGameRewardsIndex?.externalId,
      value: contestGameRewardsIndex?.value,
      validityInDays: contestGameRewardsIndex?.validityInDays,
      minimumOrderAmount: contestGameRewardsIndex?.minimumOrderAmount
    }

    contest.value.contestGameRewards.push(duplicatedContest)
    calculateSumPercentage()
  }
}
const slotNames = (name: string, index: number) => {
  return `${name}-${index}`
}

const openedRewardModalId = ref<number>(null)
const selectedReward = ref<ContestGameReward>(null)

const openRewardModal = (reward: ContestGameReward, index: number) => {
  openedRewardModalId.value = index
  selectedReward.value = reward
}

const isRewardPersonalized = (reward) => {
  const rewardOverrideIsNull = reward.notificationTextOverride?.length === 0 || reward.notificationTextOverride === null
  const isNotDefault = reward.notificationTextOverride !== contest.value.messageTemplate.content
  const isNotQrCodeDefault = reward.notificationTextOverride !== contest.value.messageTemplateQrcode.content

  if (rewardOverrideIsNull) {
    return false
  }
  return reward.hasQrcode ? isNotQrCodeDefault : isNotDefault
}

watch(
  () => computedWheelRewards.value,
  () => calculateSumPercentage()
)
watch(
  () => props.getContest,
  () => (contest.value = props.getContest)
)
</script>
<template>
  <BaseTip severity="info" :text="t('pages.contests.contestsCreatePage.legacyDisclamer')" class="mb-6" />
  <div class="mb-12 rounded-md border bg-white px-8 py-6">
    <div class="flex flex-wrap justify-between border-b pb-6">
      <div>
        <h3 class="font-medium text-foreground">
          {{ t('pages.contests.contestsCreatePage.prices.title') }}
        </h3>
        <p class="mt-1 pr-12 text-xs font-normal text-muted-foreground">
          {{ t('pages.contests.contestsCreatePage.prices.description') }}
        </p>
      </div>
      <div class="flex flex-col">
        <BaseTip
          v-if="sumPercentage < 100 && sumPercentage !== 0"
          severity="warn"
          :text="
            $t('pages.contests.contestsCreatePage.prices.form.price.not100Error', {
              sumPercentage: parseFloat((100 - sumPercentage).toFixed(4))
            })
          "
        />
        <BaseTip
          v-if="sumPercentage === 100"
          severity="success"
          :text="$t('pages.contests.contestsCreatePage.prices.form.price.100percentage')"
        />
        <BaseTip
          v-if="sumPercentage > 100"
          severity="error"
          :text="
            $t('pages.contests.contestsCreatePage.prices.form.price.moreThan100', {
              sumPercentage: parseFloat((sumPercentage - 100).toFixed(4))
            })
          "
        />
      </div>
    </div>
    <ProductTile :items-length="cardLength" class="py-6" @delete="removeReward">
      <template v-for="(reward, index) in contest.contestGameRewards" #[slotNames(`header`,index)] :key="index">
        <div class="text-foreground">
          <div
            v-if="reward.rewardTypeSlug !== 'points' || (reward.rewardTypeSlug === null && reward.points === null)"
            class="flex items-center gap-1"
          >
            <BaseIcon name="HeroGiftIconSolid" class="h-4 w-4" />
            <p>
              {{ t(`pages.contests.contestsCreatePage.prices.form.price.card.gift`) }}
              <span v-if="isRewardPersonalized(reward)" class="text-sm italic">
                ({{ t(`pages.contests.contestsCreatePage.prices.form.price.card.personalized`) }})
              </span>
            </p>
          </div>
          <div
            v-else-if="reward.rewardTypeSlug === 'points' || (reward.rewardTypeSlug === null && reward.points === 0)"
            class="flex items-center gap-1"
          >
            <BaseIcon name="HeroUserPlusIconSolid" class="h-4 w-4" />
            <p>
              {{ t(`pages.contests.contestsCreatePage.prices.form.price.card.points`) }}
              <span v-if="isRewardPersonalized(reward)" class="text-sm italic">
                ({{ t(`pages.contests.contestsCreatePage.prices.form.price.card.personalized`) }})
              </span>
            </p>
          </div>
        </div>
      </template>
      <template #headerRight="{ indexCard }">
        <BaseIcon
          v-if="indexCard !== contest.contestGameRewards.length"
          name="HeroSquare2StackIconSolid"
          class="w-4 cursor-pointer text-right text-foreground"
          @click="duplicateCard(indexCard)"
        />
      </template>
      <template #[slotNames(`header`,contest.contestGameRewards.length)]>
        <div class="flex items-center gap-1 text-foreground">
          <BaseIcon name="HeroNoSymbolIconSolid" class="h-4 w-4" />
          <p>{{ t(`pages.contests.contestsCreatePage.prices.form.price.card.loose`) }}</p>
        </div>
      </template>
      <template v-for="(reward, index) in contest.contestGameRewards" #[slotNames(`body`,index)] :key="index">
        <div class="h-full">
          <div class="flex h-full flex-col justify-between p-3">
            <div>
              <label
                :for="`gameReward.${index}.name`"
                class="flex items-center text-sm font-medium text-foreground"
                :class="
                  contest.contestGameRewards[index].name.length > rewardNameMaximumCharacters
                    ? 'text-red-700'
                    : 'text-foreground'
                "
              >
                {{ t('pages.contests.contestsCreatePage.prices.form.price.name') }}
              </label>
              <input
                :id="`gameReward.${index}.name`"
                :ref="`gameReward.${index}.name`"
                v-model="contest.contestGameRewards[index].name"
                type="text"
                :maxlength="rewardNameMaximumCharacters"
                class="mt-1 block w-full rounded-control border border-input px-3 py-2 focus:border-primary focus:outline-hidden focus:ring-0 sm:text-sm"
                :class="
                  contest.contestGameRewards[index].name.length > rewardNameMaximumCharacters
                    ? 'border-red-300'
                    : 'border-border'
                "
              />
              <p
                class="mt-2 text-sm text-muted-foreground"
                :class="
                  contest.contestGameRewards[index].name.length > rewardNameMaximumCharacters
                    ? 'text-red-700'
                    : 'text-muted-foreground'
                "
              >
                {{ contest.contestGameRewards[index].name.length }}/{{ rewardNameMaximumCharacters }}
                {{ t('global.words.characters') }}
              </p>
            </div>

            <div v-if="reward.points !== null">
              <label :for="`gameReward.${index}.points`" class="flex items-center text-sm font-medium text-foreground">
                {{ t('pages.contests.contestsCreatePage.prices.form.price.addPoints.label') }}
              </label>
              <input
                :id="`gameReward.${index}.points`"
                :ref="`gameReward.${index}.points`"
                v-model="contest.contestGameRewards[index].points"
                type="number"
                min="0"
                class="mt-1 block w-full rounded-control border border-input px-3 py-2 focus:border-primary focus:outline-hidden focus:ring-0 sm:text-sm"
              />
            </div>

            <div>
              <label
                :for="`gameReward.${index}.probability`"
                class="flex items-center text-sm font-medium"
                :class="
                  probabilityHasError(contest.contestGameRewards[index].probability)
                    ? 'text-red-700'
                    : 'text-foreground'
                "
              >
                {{ t('pages.contests.contestsCreatePage.prices.form.price.oddsOfWinning') }}
              </label>

              <div class="mt-1 flex rounded-md">
                <input
                  :id="`gameReward.${index}.probability`"
                  v-model="contest.contestGameRewards[index].probability"
                  type="number"
                  min="0"
                  step="0.0001"
                  class="block h-10 w-full rounded-l-control border px-3 py-2 focus:border-primary focus:outline-hidden focus:ring-0 sm:text-sm"
                  :class="
                    probabilityHasError(contest.contestGameRewards[index].probability)
                      ? 'border-red-300'
                      : 'border-border'
                  "
                  @input="calculateSumPercentage"
                />
                <span
                  class="inline-flex items-center rounded-r-control border border-l-0 px-3 sm:text-sm"
                  :class="
                    probabilityHasError(contest.contestGameRewards[index].probability)
                      ? 'border-red-300 text-red-500 bg-red-50'
                      : 'border-border text-muted-foreground bg-muted'
                  "
                >
                  %
                </span>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template v-if="contest.canLose" #[slotNames(`body`,contest.contestGameRewards.length)]>
        <div class="flex h-full flex-col justify-between p-3">
          <div>
            <label
              for="contest.losingTileText"
              class="flex items-center text-sm font-medium text-foreground"
              :class="contest.losingTileText.length > rewardNameMaximumCharacters ? 'text-red-700' : 'text-foreground'"
            >
              {{ t('pages.contests.contestsCreatePage.prices.form.loose.losingTileText') }}
            </label>
            <input
              id="contest.losingTileText"
              ref="contest.losingTileText"
              v-model="contest.losingTileText"
              type="text"
              :maxlength="rewardNameMaximumCharacters"
              class="mt-1 block w-full rounded-control border border-input px-3 py-2 focus:border-primary focus:outline-hidden focus:ring-0 sm:text-sm"
              :class="contest.losingTileText.length > rewardNameMaximumCharacters ? 'border-red-300' : 'border-border'"
            />
            <p
              class="mt-2 text-sm text-muted-foreground"
              :class="
                contest.losingTileText.length > rewardNameMaximumCharacters ? 'text-red-700' : 'text-muted-foreground'
              "
            >
              {{ contest.losingTileText.length }}/{{ rewardNameMaximumCharacters }}
              {{ t('global.words.characters') }}
            </p>
          </div>

          <div class="mb-10">
            <label
              for="contest.losingProbability"
              class="flex items-center text-sm font-medium"
              :class="probabilityHasError(contest.losingProbability) ? 'text-red-700' : 'text-foreground'"
            >
              {{ t('pages.contests.contestsCreatePage.prices.form.loose.losingProbability') }}
            </label>

            <div class="mt-1 flex rounded-md">
              <input
                id="contest.losingProbability"
                v-model="contest.losingProbability"
                type="number"
                min="0"
                step="0.0001"
                class="block h-10 w-full rounded-l-control border px-3 py-2 focus:border-primary focus:outline-hidden focus:ring-0 sm:text-sm"
                :class="probabilityHasError(contest.losingProbability) ? 'border-red-300' : 'border-border'"
                @input="calculateSumPercentage"
              />
              <span
                class="inline-flex items-center rounded-r-control border border-l-0 px-3 sm:text-sm"
                :class="
                  probabilityHasError(contest.losingProbability)
                    ? 'border-red-300 text-red-500 bg-red-50'
                    : 'border-border text-muted-foreground bg-muted'
                "
              >
                %
              </span>
            </div>
          </div>
        </div>
      </template>
      <template v-for="(reward, index) in contest.contestGameRewards" #[slotNames(`footer`,index)] :key="index">
        <div
          class="flex cursor-pointer items-center justify-center border-t py-2 text-foreground"
          @click="openRewardModal(reward, index)"
        >
          <BaseIcon name="HeroCog6ToothIconOutline" class="mr-2 h-5 w-5" />
          {{ t('pages.contests.contestsCreatePage.prices.form.price.buttons.personalize') }}
        </div>
        <BaseModal
          :key="index"
          :heading-title="t('pages.contests.contestsCreatePage.prices.modal.title')"
          :is-open="openedRewardModalId === index"
          :show-footer="false"
          @close="openedRewardModalId = null"
        >
          <RewardModal
            :reward="selectedReward"
            usage="contest_game"
            :message-template="contest.messageTemplate"
            :message-template-qrcode="contest.messageTemplateQrcode"
            @close="openedRewardModalId = null"
            @update:reward="(newReward) => updateReward(newReward, index)"
          />
        </BaseModal>
      </template>
      <template #addCard>
        <BaseIcon name="HeroGiftIconSolid" class="w-10 text-foreground-disabled" />
        <p class="text-sm text-foreground-disabled">
          {{ t('pages.contests.contestsCreatePage.prices.form.price.buttons.addAPrice') }}
        </p>
      </template>
      <template #buttons>
        <div class="grid w-full grid-cols-1 flex-wrap gap-3 p-6 lg:grid-cols-2">
          <Button variant="default-primary" :disabled="!canAddMoreReward" @click="addReward('points')">
            <BaseIcon name="HeroUserPlusIconSolid" class="h-5 w-5" />
            <p class="text-sm">
              {{ t('pages.contests.contestsCreatePage.prices.form.price.card.points') }}
            </p>
          </Button>
          <Button variant="default-primary" :disabled="!canAddMoreReward" @click="addReward('gift')">
            <BaseIcon name="HeroGiftIconSolid" class="h-5 w-5" />
            <p class="text-sm">
              {{ t('pages.contests.contestsCreatePage.prices.form.price.card.gift') }}
            </p>
          </Button>
          <Button variant="default-primary" :disabled="contest.canLose || !canAddMoreReward" @click="addLosingCard">
            <BaseIcon name="HeroNoSymbolIconSolid" class="h-5 w-5" />
            <p class="text-sm">
              {{ t('pages.contests.contestsCreatePage.prices.form.price.card.loose') }}
            </p>
          </Button>
        </div>
      </template>
    </ProductTile>
  </div>
</template>
